'use client'

/**
 * global-error.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Root-level error boundary for the entire Next.js app.  This catches any
 * unhandled errors that bubble past the (app)/error.tsx segment boundary —
 * including errors thrown in the root layout itself (e.g. font loading,
 * SpeedInsights, or a crash in a Provider that wraps the whole tree).
 *
 * Unlike (app)/error.tsx, this component MUST render its own <html> and <body>
 * tags because it replaces the root layout when it activates.
 *
 * See: https://nextjs.org/docs/app/building-your-application/routing/error-handling#handling-global-errors
 */

import { useEffect } from 'react'
import { reportClientError } from '@/lib/reportClientError'
import { addBreadcrumb } from '@/lib/breadcrumbs'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Same rationale as app/(app)/error.tsx — log the boundary trigger as a
    // breadcrumb so subsequent errors (after Try again / Go home) carry the
    // chain of failures in their report.
    addBreadcrumb({
      category: 'custom',
      message:  'global_error_boundary',
      data:     { msg: (error.message || '').slice(0, 120), digest: error.digest },
    })
    console.error('[obi-hub] global error:', error)
    reportClientError({
      error_type:      'react_boundary',
      message:         error.message || 'Global error boundary triggered',
      stack:           error.stack ?? null,
      component_stack: null,
    })
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          backgroundColor: '#fafafa',
          color: '#1a1a1a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            padding: 32,
            maxWidth: 480,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              backgroundColor: '#f5f5f5',
              border: '1px solid #e5e5e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 700,
              color: '#888',
            }}
          >
            !
          </div>

          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            Something went wrong
          </h1>

          <p style={{ margin: 0, fontSize: 14, color: '#666', lineHeight: 1.6 }}>
            An unexpected error occurred. This has been logged automatically.
            Try refreshing the page — if the problem persists, please contact
            support.
          </p>

          {error.digest && (
            <p
              style={{
                margin: 0,
                fontSize: 11,
                color: '#aaa',
                fontFamily: 'monospace',
              }}
            >
              Error ID: {error.digest}
            </p>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              onClick={reset}
              style={{
                padding: '8px 20px',
                fontSize: 14,
                fontWeight: 500,
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                backgroundColor: '#fff',
                color: '#1a1a1a',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => { window.location.href = '/home' }}
              style={{
                padding: '8px 20px',
                fontSize: 14,
                fontWeight: 500,
                border: 'none',
                borderRadius: 8,
                backgroundColor: '#1a1a1a',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Go to home
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
